"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Plane, Calendar as CalendarIcon, User, MapPin, Phone, Mail, Users, FileText, Loader2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BookYourPickup() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="55444" id="contact" className="relative min-h-screen w-full flex items-center justify-center py-20 px-4 overflow-hidden dark bg-background text-foreground">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/dubai_airport_terminal_night.png)]" />
        <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-[2px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary/10 blur-[120px] rounded-full -mr-64 -mb-64" />
      </div>

      <div ref={ref} className={`relative z-10 w-full max-w-5xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="mb-10 text-center">
          <h2 className="text-4xl md:text-6xl font-default font-bold text-white mb-4 tracking-tight">
            Executive Ground Transportation
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto font-default mb-6">
            Elite chauffeur services for discerning travelers. Experience the gold standard in airport transfers and corporate logistics.
          </p>
          <div className="inline-block px-4 py-2 bg-white/5 border border-white/10 rounded-full">
            <span className="text-primary font-mono text-sm tracking-widest uppercase">
              Serving SFO • OAK • SJC
            </span>
          </div>
        </div>

        {}
        <div className="w-full bg-slate-950/40 backdrop-blur-2xl border border-white/5 rounded-none shadow-2xl overflow-hidden">
          {}
          <div className="bg-primary/5 px-6 py-5 flex items-center gap-3 border-b border-primary/20">
            <Plane className="text-primary size-5" />
            <span className="text-primary font-default font-bold tracking-widest uppercase text-xs">
              Instant Quote Request
            </span>
          </div>

          <div className="p-6 md:p-10">
            <FormProvider formKey="contact_form_ourfleetaboutusandcontact" formId="contact_form_ourfleetaboutusandcontact" sectionName="book-your-pickup" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            arrival_date: "",
            flight_number: "",
            pickup_location: "",
            passenger_count: "",
            special_requests: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true,
              pattern: "phone"
            },
            arrival_date: {
              required: true
            },
            flight_number: {
              required: true,
              pattern: "^[A-Z]{2}\\s?\\d{1,4}$"
            },
            pickup_location: {
              required: true
            },
            passenger_count: {
              required: true,
              min: 1,
              max: 10
            },
            special_requests: {
              required: false
            }
          }} submitText="Request Quote" successMessage="Your quote request has been received. Our concierge team will contact you shortly." errorMessage="There was an issue processing your request. Please try again or contact us directly." className="w-full">
              <BookingFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function BookingFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-2">
          <Plane className="size-8 text-primary" />
        </div>
        <h3 className="text-3xl font-default font-bold text-white">Quote Request Received</h3>
        <p className="text-slate-400 max-w-md font-default">{successMessage}</p>
        <Button variant="outline" className="mt-6 border-primary/50 text-primary hover:bg-primary/10 hover:text-primary transition-colors font-default" onClick={() => window.location.reload()}>
          Request Another Quote
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        
        {}
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-500 font-mono text-xs uppercase tracking-wider">Full Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-0 top-2.5 size-4 text-primary/60" />
                  <Input placeholder="John Doe" {...field} className="pl-7 bg-black/40 border-t-0 border-x-0 border-b-2 border-slate-800 rounded-none text-white placeholder:text-slate-700 focus-visible:ring-0 focus-visible:border-primary transition-all duration-300" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email_address" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-500 font-mono text-xs uppercase tracking-wider">Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-0 top-2.5 size-4 text-primary/60" />
                  <Input type="email" placeholder="john@example.com" {...field} className="pl-7 bg-black/40 border-t-0 border-x-0 border-b-2 border-slate-800 rounded-none text-white placeholder:text-slate-700 focus-visible:ring-0 focus-visible:border-primary transition-all duration-300" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-500 font-mono text-xs uppercase tracking-wider">Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-0 top-2.5 size-4 text-primary/60" />
                  <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="pl-7 bg-black/40 border-t-0 border-x-0 border-b-2 border-slate-800 rounded-none text-white placeholder:text-slate-700 focus-visible:ring-0 focus-visible:border-primary transition-all duration-300" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="flight_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-500 font-mono text-xs uppercase tracking-wider">Flight Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Plane className="absolute left-0 top-2.5 size-4 text-primary/60" />
                  <Input placeholder="SFO-123" {...field} className="pl-7 bg-black/40 border-t-0 border-x-0 border-b-2 border-slate-800 rounded-none text-white placeholder:text-slate-700 focus-visible:ring-0 focus-visible:border-primary transition-all duration-300" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="arrival_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-500 font-mono text-xs uppercase tracking-wider">Service Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <SafeHydrate><CalendarIcon className="absolute left-0 top-2.5 size-4 text-primary/60" /></SafeHydrate>
                  <Input type="date" {...field} className="pl-7 bg-black/40 border-t-0 border-x-0 border-b-2 border-slate-800 rounded-none text-white placeholder:text-slate-700 focus-visible:ring-0 focus-visible:border-primary transition-all duration-300 [color-scheme:dark]" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="passenger_count" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-500 font-mono text-xs uppercase tracking-wider">Passengers</FormLabel>
              <FormControl>
                <div className="relative">
                  <Users className="absolute left-0 top-2.5 size-4 text-primary/60" />
                  <Input type="number" placeholder="1" min={1} max={10} {...field} className="pl-7 bg-black/40 border-t-0 border-x-0 border-b-2 border-slate-800 rounded-none text-white placeholder:text-slate-700 focus-visible:ring-0 focus-visible:border-primary transition-all duration-300" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="pickup_location" render={({
        field
      }) => <FormItem className="md:col-span-1 lg:col-span-3">
              <FormLabel className="text-slate-500 font-mono text-xs uppercase tracking-wider">Arrival Airport</FormLabel>
              <FormControl>
                <div className="relative">
                  <MapPin className="absolute left-0 top-3 size-4 text-primary/60 pointer-events-none" />
                  <select {...field} className="flex h-10 w-full rounded-none border-t-0 border-x-0 border-b-2 border-slate-800 bg-black/40 px-3 py-2 pl-7 text-sm text-white ring-offset-background placeholder:text-slate-700 focus-visible:outline-none focus-visible:ring-0 focus-visible:border-primary transition-all duration-300 appearance-none">
                    <option value="" disabled className="bg-slate-950 text-slate-500">Select Airport</option>
                    <option value="San Francisco International (SFO)" className="bg-slate-950">San Francisco International (SFO)</option>
                    <option value="Oakland International (OAK)" className="bg-slate-950">Oakland International (OAK)</option>
                    <option value="San Jose Mineta (SJC)" className="bg-slate-950">San Jose Mineta (SJC)</option>
                    <option value="Other Bay Area Location" className="bg-slate-950">Other Bay Area Location</option>
                  </select>
                  <div className="absolute right-0 top-3 pointer-events-none">
                    <svg className="size-4 text-primary/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="special_requests" render={({
        field
      }) => <FormItem className="md:col-span-2 lg:col-span-3">
              <FormLabel className="text-slate-500 font-mono text-xs uppercase tracking-wider">Additional Requirements</FormLabel>
              <FormControl>
                <div className="relative">
                  <FileText className="absolute left-0 top-3 size-4 text-primary/60" />
                  <textarea {...field} placeholder="Enter any special requests, vehicle preferences, or additional notes..." className="flex min-h-[80px] w-full rounded-none border-t-0 border-x-0 border-b-2 border-slate-800 bg-black/40 px-3 py-2 pl-7 text-sm text-white ring-offset-background placeholder:text-slate-700 focus-visible:outline-none focus-visible:ring-0 focus-visible:border-primary transition-all duration-300" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none text-destructive text-sm font-default">
          {submitError}
        </div>}

      <div className="flex justify-end pt-4">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-default font-bold uppercase tracking-widest transition-all duration-300 shadow-xl shadow-primary/5">
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Processing Quote...
            </> : "Request Quote"}
        </Button>
      </div>
    </Form>;
}